<template>
  <v-app data-draft-reminder>
    <v-banner
      v-model="isVisible"
      class="draft-reminder"
      :class="{ 'draft-reminder_desktop b-container mb-5': !isMobile }"
      elevation="24"
    >
      <div
        class="d-flex justify-space-between"
        :class="{ 'flex-column': isMobile }"
      >
        <div
          class="d-flex"
          :class="{
            'align-center': !isMobile,
            'pt-2': isMobile,
          }"
        >
          <LpuLogoPicture
            v-if="showLpuLogoPicture"
            img-class="draft-reminder__avatar draft-reminder__avatar_lpu"
            :files="lpuLogoFiles"
            :fallback-src="storageData.reviewObject.avatarUrl"
            :alt="storageData.reviewObject.name"
          />
          <img
            v-else
            class="draft-reminder__avatar"
            :class="{ 'draft-reminder__avatar_lpu': !isReviewObjectDoctor }"
            :alt="storageData.reviewObject.name"
            :src="storageData.reviewObject.avatarUrl"
          >
          <div
            class="draft-reminder_text text-body-2"
            :class="{
              'ml-4': isMobile,
              'ml-6': !isMobile,
            }"
          >
            {{ $t('У вас остался незавершённый отзыв о', { ns: $i18next.language }) }} {{ reviewObjectTypeText }}
            <span
              class="text-subtitle-2"
              :class="{ 'text-no-wrap': isReviewObjectDoctor }"
            >
              {{ reviewObjectNameText }}{{ !isMobile ? '.' : '' }}
            </span>
            <br v-if="isMobile">{{ $t('Хотите дописать его и поделиться мнением с пациентами', { ns: $i18next.language }) }}?
          </div>
        </div>
        <div class="d-flex align-center justify-end">
          <v-btn
            text
            class="px-2"
            color="text"
            @click="handleClickNotNow"
          >
            {{ $t('Не сейчас', { ns: $i18next.language }) }}
          </v-btn>
          <v-btn
            text
            class="px-2 ml-2"
            :class="{ 'mr-2': !isMobile }"
            color="primary"
            :href="newRateUrl"
            @click="handleClickGoToReview"
          >
            {{ $t('К отзыву', { ns: $i18next.language }) }}
          </v-btn>
        </div>
      </div>
    </v-banner>
  </v-app>
</template>

<script>
import Cookies from 'js-cookie'
import { axiosClient } from 'www/utils'
import { differenceInHours, differenceInMinutes } from 'date-fns'
import UserStorage from 'www/modules/UserStorage'
import { getLpuLogoImageFiles } from 'src/utils/getLpuAvatarUrl'
import LpuLogoPicture from 'src/components/common/LpuLogoPicture/LpuLogoPicture.vue'
import {
  STORAGE_KEY,
  HOURS_BETWEEN_DISPLAYS,
  MINUTES_BEFORE_FIRST_DISPLAY,
  MAX_NUMBER_OF_DISPLAYS,
  REVIEW_OBJECT_TYPES,
  AJAX_URL_IS_VISIBLE,
} from 'www/vue_components/common/DraftReminder/constants'
import ManageYandexGoals from 'www/vue_components/common/mixins/ManageYandexGoals'
import { DRAFT_REMINDER_YANDEX_GOALS } from 'www/yandexGoals'

export default {
  name: 'DraftReminder',
  components: {
    LpuLogoPicture,
  },
  mixins: [
    ManageYandexGoals,
  ],
  data: () => ({
    isVisible: false,
    storageData: {
      reviewObject: {
        type: '',
        name: '',
        id: 0,
        avatarUrl: '',
        logo: {},
      },
      createdAt: 0,
      lastDisplayedAt: 0,
      displaysCount: 0,
    },
    cookieUserkey: Cookies.get('userkey'),
    isMobile: window.MOBILE_VERSION,
    yaGoalsList: {
      ...DRAFT_REMINDER_YANDEX_GOALS,
    },
  }),
  computed: {
    newRateUrl() {
      const reviewObjectType = REVIEW_OBJECT_TYPES[this.storageData.reviewObject.type]

      if (!reviewObjectType) {
        return ''
      }

      if (this.$i18next.language !== window.LANGUAGE_DATA.defaultLanguageIso) {
        return `/${this.$i18next.language}/new/rate/${reviewObjectType}/${this.storageData.reviewObject.id}/`
      }

      return `/new/rate/${reviewObjectType}/${this.storageData.reviewObject.id}/`
    },
    isReviewObjectDoctor() {
      return this.storageData.reviewObject.type === REVIEW_OBJECT_TYPES.doctor
    },
    reviewObjectTypeText() {
      return this.isReviewObjectDoctor
        ? this.$t('враче', { ns: this.$i18next.language })
        : this.$t('клинике', { ns: this.$i18next.language })
    },
    reviewObjectNameText() {
      return `${this.storageData.reviewObject.name}${this.isReviewObjectDoctor ? '' : '.'}`
    },
    showLpuLogoPicture() {
      return Boolean(window.FEATURE_FLAGS?.ff_update_lpu_logo) && !this.isReviewObjectDoctor
    },
    lpuLogoFiles() {
      return getLpuLogoImageFiles({ logo: this.storageData.reviewObject.logo })
    },
  },
  watch: {
    storageData: {
      deep: true,
      handler(newStorageData) {
        try {
          UserStorage.set(STORAGE_KEY, newStorageData)
        } catch {
          UserStorage.remove(STORAGE_KEY)
        }
      },
    },
  },
  mounted() {
    if (!this.cookieUserkey) {
      return
    }

    const storageData = UserStorage.get(STORAGE_KEY)

    if (!storageData || typeof storageData !== 'object' || !storageData.reviewObject) {
      return
    }

    const currentDate = Date.now()
    const { createdAt, lastDisplayedAt, displaysCount } = storageData

    const isTooEarlyToShow = differenceInMinutes(currentDate, createdAt) < MINUTES_BEFORE_FIRST_DISPLAY
            || differenceInHours(currentDate, lastDisplayedAt) < HOURS_BETWEEN_DISPLAYS

    if (isTooEarlyToShow || displaysCount >= MAX_NUMBER_OF_DISPLAYS) {
      return
    }

    this.storageData = storageData

    const userData = {
      userkey: this.cookieUserkey,
      object_type: storageData.reviewObject.type,
      object_id: storageData.reviewObject.id,
    }

    axiosClient.post(AJAX_URL_IS_VISIBLE, userData)
      .then(({ data }) => {
        const { result: isVisible } = data

        if (isVisible) {
          this.isVisible = true

          this.sendYandexGoal('show')
        } else {
          UserStorage.remove(STORAGE_KEY)
        }

        return null
      })
      .catch(() => {
        UserStorage.remove(STORAGE_KEY)
      })
  },
  methods: {
    handleClickNotNow() {
      this.isVisible = false
      this.storageData.displaysCount++
      this.storageData.lastDisplayedAt = Date.now()

      this.sendYandexGoal('notNow')
    },
    handleClickGoToReview() {
      this.sendYandexGoal('goToReview')
    },
  },
}
</script>

<style lang="scss">
@import '~www/themes/doctors/common/variables';
@import '~www/themes/doctors/common/mixins';

#app[data-draft-reminder] {
  @include v-app-reset;

  .draft-reminder {
    position: fixed;
    bottom: 0;
    z-index: 201;
    background-color: $ui-kit-bg-gray-0;

    &_desktop {
      left: 50%;
      transform: translateX(-50%);

      .draft-reminder_text {
        width: 80%;
      }
    }

    &__avatar {
      width: 60px;
      height: 80px;
      object-fit: contain;
      border-radius: $border-radius-sm;
    }

    &__avatar_lpu {
      height: 60px;
    }
  }
}
</style>
